import { AlertCircle, CheckCircle, Clock, Ticket as TicketIcon } from 'lucide-react'

interface SupportTicket {
  id: number
  subject: string
  description: string
  category: string
  priority: 'low' | 'medium' | 'high'
  status: 'open' | 'in-progress' | 'resolved'
  created_at: string
  user_id?: number | null
}

interface TicketListProps {
  tickets: SupportTicket[]
  onClose: () => void
}

export default function TicketList({ tickets, onClose }: TicketListProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle size={16} color="#f59e0b" />
      case 'in-progress':
        return <Clock size={16} color="#3b82f6" />
      case 'resolved':
        return <CheckCircle size={16} color="#10b981" />
      default:
        return <TicketIcon size={16} />
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open':
        return 'Açık'
      case 'in-progress':
        return 'İşlemde'
      case 'resolved':
        return 'Çözüldü'
      default:
        return status
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#ef4444'
      case 'medium':
        return '#f59e0b'
      case 'low':
        return '#10b981'
      default:
        return '#6b7280'
    }
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1a1d29', marginBottom: '4px' }}>
              Destek Talepleri
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              {tickets.length} talep
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              background: '#fff',
              color: '#6b7280',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Kapat
          </button>
        </div>

        {/* Tickets */}
        <div style={{ padding: '24px' }}>
          {tickets.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#6b7280'
            }}>
              <TicketIcon size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
              <p style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
                Henüz destek talebi yok
              </p>
              <p style={{ fontSize: '14px' }}>
                Sorun yaşadığınızda destek talebi oluşturabilirsiniz
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  style={{
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    transition: 'all 0.2s'
                  }}
                >
                  {/* Ticket Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px'
                      }}>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#6b7280',
                          fontFamily: 'monospace'
                        }}>
                          #{ticket.id}
                        </span>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          background: '#f8fafc',
                          fontSize: '12px',
                          fontWeight: 600
                        }}>
                          {getStatusIcon(ticket.status)}
                          <span>{getStatusLabel(ticket.status)}</span>
                        </div>
                      </div>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: '#1a1d29',
                        marginBottom: '8px'
                      }}>
                        {ticket.subject}
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        lineHeight: 1.6
                      }}>
                        {ticket.description}
                      </p>
                    </div>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: getPriorityColor(ticket.priority),
                      marginLeft: '12px',
                      marginTop: '4px'
                    }} />
                  </div>

                  {/* Ticket Footer */}
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    fontSize: '12px',
                    color: '#94a3b8',
                    paddingTop: '12px',
                    borderTop: '1px solid #f1f5f9'
                  }}>
                    <span>
                      Kategori: <strong>{ticket.category}</strong>
                    </span>
                    <span>
                      Öncelik: <strong style={{ color: getPriorityColor(ticket.priority) }}>
                        {ticket.priority === 'low' ? 'Düşük' : ticket.priority === 'medium' ? 'Orta' : 'Yüksek'}
                      </strong>
                    </span>
                    <span>
                      {new Date(ticket.created_at).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
